/* The figures a return needs, gathered from the book.
 *
 * It reports. It does not say what anyone owes, and it is not advice — the rule
 * about advice (§1) matters more here than anywhere, because a confident wrong
 * number in this particular place costs money. Every figure is shown against the
 * account and currency it came from so it can be checked line by line against
 * the broker's own statement. */

import { useMemo, useState } from 'react';
import { buildTaxReport, convertYear, ratesNeeded, type Converted } from '../state/taxReport';
import { fifoRealised } from '../state/fifo';
import * as api from '../api/client';
import { moneyExact, pct } from '../lib/format';
import { NumberField } from './NumberField';
import { ratesFor, regimeOf, REGIMES, taxDue } from '../rules/tax';
import { useStore } from '../state/store';

type Table = { usdPln?: number; eurPln?: number; hkdPln?: number };

/* PLN per one unit of the currency, from that day's table. A rate the table did
   not carry answers null, and whatever needed it is left out rather than
   converted at something else. */
const RATE_FOR: Record<string, (fx: Table) => number | null> = {
  PLN: () => 1,
  USD: (fx) => fx.usdPln ?? null,
  EUR: (fx) => fx.eurPln ?? null,
  HKD: (fx) => fx.hkdPln ?? null,
};

/* The charge on the converted year — the one a Polish return is actually filed
   on, because every amount in it has been through the rate for the day before
   its own transaction. Still arithmetic on the user's own records at a rate the
   user set: it is not a computed liability and the screen says so beneath. */
function ConvertedCharge({ converted, rates }: {
  converted: { realised: number; dividendGross: number; dividendWithheld: number; skipped: number };
  rates: ReturnType<typeof ratesFor>;
}) {
  const due = taxDue({
    realisedGain: converted.realised,
    dividendGross: converted.dividendGross,
    dividendWithheld: converted.dividendWithheld,
    rates,
  });

  if (due.total === null) {
    return (
      <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.45 }}>
        Set a rate above and this becomes a figure. It is left blank rather than shown as zero,
        because nothing here says the charge is nothing.
      </div>
    );
  }

  return (
    <>
      <div className="divider" />
      <div className="kv">
        <span className="k">On gains, at {pct(rates.gainsPct!)}</span>
        <span className="v num">{moneyExact(due.onGains!, 'PLN')}</span>
      </div>
      <div className="kv">
        <span className="k">On dividends, at {pct(rates.dividendPct!)}</span>
        <span className="v num">{moneyExact(due.onDividends!, 'PLN')}</span>
      </div>
      {due.credited > 0 ? (
        <div className="kv">
          <span className="k">Less withheld abroad</span>
          <span className="v num">−{moneyExact(due.credited, 'PLN')}</span>
        </div>
      ) : null}
      <div className="kv" style={{ marginTop: 6 }}>
        <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>
          At the rates you set
        </span>
        <span className="v num" style={{ fontWeight: 600 }}>{moneyExact(due.total, 'PLN')}</span>
      </div>
      {converted.skipped > 0 ? null : (
        <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.45 }}>
          Your figures multiplied by your rate. Whether that is what you owe depends on
          allowances, losses carried forward and everything else on your return, none of which
          is in this book.
        </div>
      )}
    </>
  );
}

/* One broker's charge at the rates in force. Shown in that broker's own
   currency, and only where a rate exists — an unset rate prints as unset, never
   as a charge of nothing. */
function LineCharge({ line, rates }: {
  line: { realised: number; dividendGross: number; dividendWithheld: number; currency: string };
  rates: ReturnType<typeof ratesFor>;
}) {
  const due = taxDue({
    realisedGain: line.realised,
    dividendGross: line.dividendGross,
    dividendWithheld: line.dividendWithheld,
    rates,
  });

  if (due.total === null) {
    return (
      <div className="kv" style={{ marginTop: 8 }}>
        <span className="k">At your rate</span>
        <span className="v dim" style={{ fontSize: 12 }}>no rate set</span>
      </div>
    );
  }

  return (
    <>
      <div className="divider" />
      <div className="kv">
        <span className="k">
          Tax at {pct(rates.gainsPct!)}
          {rates.dividendPct !== rates.gainsPct ? ` and ${pct(rates.dividendPct!)}` : ''}
        </span>
        <span className="v num">{moneyExact(due.total, line.currency as never)}</span>
      </div>
      {due.credited > 0 ? (
        <div className="dim" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
          After {moneyExact(due.credited, line.currency as never)} already withheld at source.
        </div>
      ) : null}
    </>
  );
}

export function TaxReport({ say }: { say: (m: string) => void }) {
  const { state, saveSettings } = useStore();
  const now = new Date().getUTCFullYear();
  const [year, setYear] = useState(now);
  const [converted, setConverted] = useState<Converted | null>(null);
  const [busy, setBusy] = useState(false);

  const country = state.settings.taxCountry ?? 'other';
  const regime = regimeOf(country);
  const rates = ratesFor(state.settings);
  const { trades, dividends, accounts, transfers } = state.data;

  const report = useMemo(
    () => buildTaxReport({ year, trades, dividends, accounts }),
    [year, trades, dividends, accounts],
  );

  const convert = async () => {
    setBusy(true);
    setConverted(null);
    try {
      const dates = ratesNeeded(report, trades, dividends);
      const table = new Map<string, Table>();
      // Old tables never change, so the Worker caches them for a month; asking
      // for a year of dates is cheap the second time.
      for (const date of dates) {
        try { table.set(date, await api.fetchFxOn(date)); } catch { /* counted as missing */ }
      }
      setConverted(convertYear({
        year, trades, dividends, accounts,
        rate: (currency, on) => {
          const fx = table.get(on);
          if (!fx) return null;
          return RATE_FOR[currency.toUpperCase()]?.(fx) ?? null;
        },
      }));
      say(`${dates.length} daily rates read.`);
    } finally {
      setBusy(false);
    }
  };

  /* The same year matched earliest-parcel-first. The book's own figure comes
     off an average cost, which reads a position correctly and is not what a
     return asks for. Both are shown; neither is called the right one. */
  const fifo = useMemo(() => {
    const all = fifoRealised(trades, transfers);
    const inYear = all.sales.filter((sale) => sale.at.slice(0, 4) === String(year));
    const shielded = new Set(accounts.filter((a) => a.taxShielded).map((a) => a.id));
    const taxable = inYear.filter((sale) => !shielded.has(sale.account));
    return {
      gain: taxable.reduce((sum, sale) => sum + sale.gain, 0),
      sales: taxable.length,
      unmatched: all.unmatched.filter((u) => u.at.slice(0, 4) === String(year)),
    };
  }, [trades, transfers, accounts, year]);

  const years = [now, now - 1, now - 2];
  const nothing = report.lines.length === 0;

  return (
    <>
      <div className="field">
        <label htmlFor="tax-country">Where you file</label>
        <select id="tax-country" className="select" value={country}
                onChange={(e) => void saveSettings({
                  taxCountry: e.target.value,
                  /* Changing country drops any rate typed under the old one.
                     A 19% left standing after a move would be the previous
                     country's answer wearing the new country's name. */
                  taxGainsPct: undefined, taxDividendPct: undefined,
                })}>
          {REGIMES.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
        <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.45 }}>
          {regime.note}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="tax-gains">Rate on gains (%)</label>
          <NumberField id="tax-gains" value={rates.gainsPct}
                       placeholder={regime.gainsPct === null ? 'set it' : String(regime.gainsPct)}
                       onChange={(n, text) => void saveSettings({
                         taxGainsPct: text.trim() === '' || !Number.isFinite(n) ? undefined : n,
                       })} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor="tax-div">Rate on dividends (%)</label>
          <NumberField id="tax-div" value={rates.dividendPct}
                       placeholder={regime.dividendPct === null ? 'set it' : String(regime.dividendPct)}
                       onChange={(n, text) => void saveSettings({
                         taxDividendPct: text.trim() === '' || !Number.isFinite(n) ? undefined : n,
                       })} />
        </div>
      </div>
      <div className="dim" style={{ fontSize: 11, marginBottom: 12, marginTop: -2, lineHeight: 1.45 }}>
        {rates.fromRegime
          ? 'Filled in from the country above. Type over either one and yours is used instead.'
          : rates.gainsPct === null || rates.dividendPct === null
            ? 'Until both are set, the charges below read as unset rather than as zero.'
            : 'Your own rates, not the country’s. Clear a field to go back to the country’s figure.'}
      </div>

      <div className="seg" style={{ marginBottom: 12 }}>
        {years.map((y) => (
          <button key={y} aria-pressed={year === y} onClick={() => { setYear(y); setConverted(null); }}>
            {y}
          </button>
        ))}
      </div>

      {nothing ? (
        <div className="empty">
          Nothing recorded for {year}. Sales reach this once they are in the trade log, and dividends
          once they are entered.
        </div>
      ) : (
        <>
          {report.lines.map((line) => (
            <div className="card card-pad" key={`${line.account}-${line.currency}`} style={{ marginBottom: 8 }}>
              <div className="kv">
                <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>
                  {line.accountName}
                </span>
                <span className="meta" style={{ marginLeft: 'auto' }}>{line.currency}</span>
              </div>
              {line.shielded ? (
                <div className="tag" data-kind="ok" style={{ marginTop: 6 }}>SHELTERED · OUTSIDE THE TAX BASE</div>
              ) : null}
              <div className="kv" style={{ marginTop: 8 }}>
                <span className="k">Realised on {line.sales} sale{line.sales === 1 ? '' : 's'}</span>
                <span className="v num">{moneyExact(line.realised, line.currency as never)}</span>
              </div>
              {line.dividends > 0 ? (
                <>
                  <div className="kv">
                    <span className="k">Dividends gross</span>
                    <span className="v num">{moneyExact(line.dividendGross, line.currency as never)}</span>
                  </div>
                  <div className="kv">
                    <span className="k">Withheld at source</span>
                    <span className="v num">{moneyExact(line.dividendWithheld, line.currency as never)}</span>
                  </div>
                </>
              ) : null}
              {/* What that broker's year comes to at the rates set above — in
                  its own currency, because that is the currency its figures are
                  in. The charge a return actually asks for is on the converted
                  total further down; this is the per-broker breakdown, and it
                  is here so a line can be checked against one statement. */}
              {!line.shielded ? <LineCharge line={line} rates={rates} /> : null}
            </div>
          ))}

          {report.salesWithoutGain > 0 ? (
            <div className="warn" style={{ fontSize: 11.5, lineHeight: 1.45, marginBottom: 10 }}>
              {report.salesWithoutGain} sale{report.salesWithoutGain === 1 ? '' : 's'} in {year} carry
              no recorded gain, so they are not in these totals. They were counted as sales but not as
              amounts. A zero would have made the year look complete when it is not.
            </div>
          ) : null}

          <div className="divider" />

          <div className="kv">
            <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>
              Earliest parcel first
            </span>
            <span className="v num">{moneyExact(fifo.gain, 'USD')}</span>
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 6, lineHeight: 1.5 }}>
            {fifo.sales} sale{fifo.sales === 1 ? '' : 's'} matched to the shares they actually sold,
            which is what a return asks for. The figures above come off the average cost the book
            keeps, which is right for reading a position and not for this. Where a holding was bought in one
            go the two agree; where it was built up over time they will not, and neither is an
            approximation of the other.
          </div>
          {fifo.unmatched.length > 0 ? (
            <div className="warn" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
              {fifo.unmatched.length} sale{fifo.unmatched.length === 1 ? '' : 's'} in {year} have no
              purchase anywhere in the trade log: {fifo.unmatched.slice(0, 3)
                .map((u) => `${u.shares} ${u.ticker}`).join(', ')}
              {fifo.unmatched.length > 3 ? ' and others' : ''}. They are left out rather than costed
              at zero, which would report the whole proceeds as profit. Import the missing history
              first if this year is going into a return.
            </div>
          ) : null}

          <div className="divider" />

          <button className="btn btn-block" disabled={busy} onClick={() => void convert()}>
            {busy ? 'Reading the daily rates…' : 'Convert at the rate of the day before each one'}
          </button>

          {converted ? (
            <div className="card card-pad" style={{ marginTop: 10 }}>
              <div className="kv">
                <span className="k">Realised, in złoty</span>
                <span className="v num">{moneyExact(converted.realised, 'PLN')}</span>
              </div>
              <div className="kv">
                <span className="k">Dividends gross</span>
                <span className="v num">{moneyExact(converted.dividendGross, 'PLN')}</span>
              </div>
              <div className="kv">
                <span className="k">Withheld at source</span>
                <span className="v num">{moneyExact(converted.dividendWithheld, 'PLN')}</span>
              </div>
              <ConvertedCharge converted={converted} rates={rates} />
              {converted.skipped > 0 ? (
                <div className="warn" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
                  {converted.skipped} item{converted.skipped === 1 ? '' : 's'} left out
                  {converted.missing.length > 0
                    ? `: no published rate found for ${converted.missing.slice(0, 3).join(', ')}`
                    : ', and no gain was recorded against them'}.
                  Nothing was converted at another day's rate to fill the gap.
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="dim" style={{ fontSize: 11, marginTop: 12, lineHeight: 1.5 }}>
            Amounts stay in the currency they happened in. Converting a year of trades at today's
            rate would produce a figure that looks official and is wrong, so the conversion is a
            separate step and uses the National Bank's table for the business day <em>before</em>
            each transaction.
            {country === 'PL' ? (
              <>
                {' '}For a Polish return, gains and dividends are taxed at a flat 19%, tax withheld
                abroad is credited against the dividend part, and an IKE account sits outside all of
                it, which is why one is marked sheltered above.
              </>
            ) : null}
          </div>

          <div className="warn" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.45 }}>
            These are your own records added up, nothing more. This is not tax advice and the app
            does not file anything. Check every line against your broker's statement before it goes
            anywhere near a return.
          </div>
        </>
      )}
    </>
  );
}
